import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import { passService, PassOTPVerifyResponse } from '../services/services/passService';
import { useConfirmation } from '../contexts/ConfirmationContext';
import InternationalPhoneInput from '../components/common/InternationalPhoneInput';
import './SecurePassAccessPage.scss';

interface Pass {
  id: string;
  identifier: string;
  event_title: string;
  category_name: string;
  event_banner_url?: string | null;
  event_thumbnail_url?: string | null;
  created_time: string;
}

const SecurePassAccessPage: React.FC = () => {
  const { alert } = useConfirmation();
  
  const [step, setStep] = useState<'contact' | 'otp' | 'passes'>('contact');
  const [contactMethod, setContactMethod] = useState<'email' | 'phone'>('email');
  const [contactValue, setContactValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [passes, setPasses] = useState<Pass[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [passesCount, setPassesCount] = useState(0);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = contactMethod === 'email' ? contactValue : phoneValue;
    
    if (!value.trim()) {
      setError('Please enter your contact information');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await passService.sendPassOTP(contactMethod, value);
      
      if (result.success) {
        setPassesCount(result.passes_count || 0);
        setStep('otp');
        alert({
          message: result.message,
          variant: 'success',
        });
      } else {
        setError(result.message || 'Failed to send OTP');
      }
    } catch (err: any) {
      console.error('Error sending OTP:', err);
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = contactMethod === 'email' ? contactValue : phoneValue;
    
    if (!otp.trim() || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await passService.verifyPassOTP(value, otp);
      
      if (result.success && result.passes) {
        setPasses(result.passes);
        setStep('passes');
        alert({
          message: result.message,
          variant: 'success',
        });
      } else {
        setError(result.message || 'OTP verification failed');
      }
    } catch (err: any) {
      console.error('Error verifying OTP:', err);
      setError(err.response?.data?.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPass = async (passId: string, format: 'image' | 'pdf' = 'image') => {
    const value = contactMethod === 'email' ? contactValue : phoneValue;
    
    try {
      // Generate download token
      const tokenResult = await passService.generateDownloadToken(passId, value);
      
      if (tokenResult.success && tokenResult.download_token) {
        // Download pass using token with specified format
        await passService.downloadWithOTP(passId, tokenResult.download_token, undefined, format);
        alert({
          message: `Pass downloaded successfully as ${format === 'pdf' ? 'PDF' : 'image'}`,
          variant: 'success',
        });
      } else {
        throw new Error(tokenResult.message || 'Failed to generate download token');
      }
    } catch (err: any) {
      console.error('Error downloading pass:', err);
      alert({
        message: err.message || err.response?.data?.message || 'Failed to download pass. Please try again.',
        variant: 'danger',
      });
    }
  };

  const handleReset = () => {
    setStep('contact');
    setContactValue('');
    setPhoneValue('');
    setOtp('');
    setPasses([]);
    setError(null);
    setPassesCount(0);
  };

  return (
    <div className="secure-pass-access-page" style={{ marginTop: 120, padding: '2rem 0' }}>
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            {/* Modern Header */}
            <div className="text-center mb-5">
              <div className="access-icon-wrapper mb-4">
                <div className="access-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>
              </div>
              <h1 className="display-5 fw-bold mb-3">Access Your Passes</h1>
              <p className="lead text-muted">Securely verify your identity to view and download your tickets</p>
            </div>

            {/* Step 1: Contact Information */}
            {step === 'contact' && (
              <>
                <div className="step-indicator mb-4">
                  <div className="step-item active">
                    <div className="step-number">1</div>
                    <div className="step-label">Contact</div>
                  </div>
                  <div className="step-connector"></div>
                  <div className="step-item">
                    <div className="step-number">2</div>
                    <div className="step-label">Verify</div>
                  </div>
                  <div className="step-connector"></div>
                  <div className="step-item">
                    <div className="step-number">3</div>
                    <div className="step-label">Access</div>
                  </div>
                </div>

                <div className="access-card">
                  <div className="card-header">
                    <div className="header-content">
                      <div className="header-icon">
                        <i className="fas fa-user-check"></i>
                      </div>
                      <div>
                        <h4 className="mb-1">Verify Your Identity</h4>
                        <p className="text-muted mb-0">Choose your preferred verification method</p>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {error && (
                      <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                      </Alert>
                    )}

                    <div className="security-info mb-4">
                      <div className="security-info-header">
                        <div className="security-icon-small">
                          <i className="fas fa-info-circle"></i>
                        </div>
                        <h5 className="mb-0">How it works</h5>
                      </div>
                      <div className="steps-list">
                        <div className="step-item-list">
                          <div className="step-number-small">1</div>
                          <span>Enter your email or phone number used during ticket purchase</span>
                        </div>
                        <div className="step-item-list">
                          <div className="step-number-small">2</div>
                          <span>Receive a verification code via email or SMS</span>
                        </div>
                        <div className="step-item-list">
                          <div className="step-number-small">3</div>
                          <span>Enter the code to access your passes</span>
                        </div>
                        <div className="step-item-list">
                          <div className="step-number-small">4</div>
                          <span>Download your passes securely</span>
                        </div>
                      </div>
                    </div>

                  {/* Contact Method Tabs */}
                  <div className="method-tabs">
                    <button
                      className={`method-tab ${contactMethod === 'email' ? 'active' : ''}`}
                      type="button"
                      onClick={() => {
                        setContactMethod('email');
                        setContactValue('');
                        setPhoneValue('');
                        setError(null);
                      }}
                    >
                      <div className="tab-icon">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <div className="tab-content">
                        <span className="tab-title">Email</span>
                        <span className="tab-subtitle">Send verification code</span>
                      </div>
                    </button>
                    <button
                      className={`method-tab ${contactMethod === 'phone' ? 'active' : ''}`}
                      type="button"
                      onClick={() => {
                        setContactMethod('phone');
                        setContactValue('');
                        setPhoneValue('');
                        setError(null);
                      }}
                    >
                      <div className="tab-icon">
                        <i className="fas fa-phone"></i>
                      </div>
                      <div className="tab-content">
                        <span className="tab-title">Phone</span>
                        <span className="tab-subtitle">Send SMS code</span>
                      </div>
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="tab-content-area">
                    {contactMethod === 'email' ? (
                      <Form onSubmit={handleContactSubmit} className="verification-form">
                        <Form.Group className="form-group">
                          <Form.Label className="form-label">
                            <i className="fas fa-envelope"></i>
                            Email Address
                          </Form.Label>
                          <Form.Control
                            type="email"
                            required
                            value={contactValue}
                            onChange={(e) => setContactValue(e.target.value)}
                            placeholder="Enter your email address"
                            className="form-control"
                          />
                          <small className="form-help">We'll send a verification code to this email address.</small>
                        </Form.Group>
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading || !contactValue.trim()}
                          className="verify-btn w-100"
                        >
                          {loading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane me-2"></i>
                              Send Verification Code
                            </>
                          )}
                        </Button>
                      </Form>
                    ) : (
                      <Form onSubmit={handleContactSubmit} className="verification-form">
                        <Form.Group className="form-group">
                          <Form.Label className="form-label">
                            <i className="fas fa-phone"></i>
                            Phone Number
                          </Form.Label>
                          <InternationalPhoneInput
                            value={phoneValue}
                            onChange={(value) => setPhoneValue(value || '')}
                            placeholder="Enter your phone number"
                          />
                          <small className="form-help">We'll send a verification code via SMS to this phone number.</small>
                        </Form.Group>
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading || !phoneValue.trim()}
                          className="verify-btn w-100"
                        >
                          {loading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-sms me-2"></i>
                              Send SMS Code
                            </>
                          )}
                        </Button>
                      </Form>
                    )}
                  </div>
                </div>
              </div>
              </>
            )}

            {/* Step 2: OTP Verification */}
            {step === 'otp' && (
              <>
                <div className="step-indicator mb-4">
                  <div className="step-item completed">
                    <div className="step-number">✓</div>
                    <div className="step-label">Contact</div>
                  </div>
                  <div className="step-connector active"></div>
                  <div className="step-item active">
                    <div className="step-number">2</div>
                    <div className="step-label">Verify</div>
                  </div>
                  <div className="step-connector"></div>
                  <div className="step-item">
                    <div className="step-number">3</div>
                    <div className="step-label">Access</div>
                  </div>
                </div>

                <div className="access-card">
                  <div className="card-header">
                    <div className="header-content">
                      <div className="header-icon">
                        <i className="fas fa-key"></i>
                      </div>
                      <div>
                        <h4 className="mb-1">Enter Verification Code</h4>
                        <p className="text-muted mb-0">Check your messages for the code</p>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                  {error && (
                    <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {error}
                    </Alert>
                  )}

                  <div className="success-alert">
                    <div className="success-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="success-content">
                      <h6>Code Sent Successfully</h6>
                      <p>Verification code sent to <strong>{contactMethod === 'email' ? contactValue : phoneValue}</strong></p>
                      {passesCount > 0 && (
                        <p className="mb-0 mt-2">
                          <strong>{passesCount} pass{passesCount !== 1 ? 'es' : ''}</strong> found for this contact.
                        </p>
                      )}
                    </div>
                  </div>

                  <Form onSubmit={handleOTPSubmit} className="verification-form">
                    <Form.Group className="form-group">
                      <Form.Label className="form-label">
                        <i className="fas fa-shield-alt"></i>
                        6-Digit Verification Code
                      </Form.Label>
                      <Form.Control
                        type="text"
                        required
                        maxLength={6}
                        value={otp}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtp(value);
                          setError(null);
                        }}
                        placeholder="000000"
                        className="form-control otp-input"
                      />
                      <small className="form-help">Enter the 6-digit code you received</small>
                    </Form.Group>

                    <div className="form-actions">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={loading || otp.length !== 6}
                        className="verify-btn primary w-100"
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check me-2"></i>
                            Verify & View Passes
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleReset}
                        className="verify-btn secondary w-100"
                      >
                        <i className="fas fa-redo me-2"></i>
                        Resend Code
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
              </>
            )}

            {/* Step 3: Passes List */}
            {step === 'passes' && (
              <>
                <div className="step-indicator mb-4">
                  <div className="step-item completed">
                    <div className="step-number">✓</div>
                    <div className="step-label">Contact</div>
                  </div>
                  <div className="step-connector active"></div>
                  <div className="step-item completed">
                    <div className="step-number">✓</div>
                    <div className="step-label">Verify</div>
                  </div>
                  <div className="step-connector active"></div>
                  <div className="step-item active">
                    <div className="step-number">3</div>
                    <div className="step-label">Access</div>
                  </div>
                </div>

                <div className="access-card">
                  <div className="card-header">
                    <div className="header-content">
                      <div className="header-icon">
                        <i className="fas fa-ticket-alt"></i>
                      </div>
                      <div>
                        <h4 className="mb-1">Your Passes</h4>
                        <p className="text-muted mb-0">Download your verified tickets</p>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                  {passes.length > 0 ? (
                    <div className="passes-grid">
                      {passes.map((pass) => (
                        <div key={pass.id} className="pass-card">
                          <div className="pass-header">
                            {(pass.event_thumbnail_url || pass.event_banner_url) ? (
                              <div className="pass-thumbnail">
                                <img 
                                  src={pass.event_thumbnail_url || pass.event_banner_url || ''} 
                                  alt={pass.event_title}
                                />
                              </div>
                            ) : (
                              <div className="pass-icon">
                                <i className="fas fa-ticket-alt"></i>
                              </div>
                            )}
                            <div className="pass-info">
                              <h5 className="pass-title">{pass.event_title}</h5>
                              <p className="pass-category">{pass.category_name}</p>
                            </div>
                            <div className="pass-status">
                              <span className="status-badge">
                                <i className="fas fa-check-circle"></i>
                                Verified
                              </span>
                            </div>
                          </div>
                          
                          <div className="pass-details">
                            <div className="detail-row">
                              <div className="detail-item">
                                <div className="detail-label">Pass ID</div>
                                <div className="detail-value pass-id">#{pass.identifier}</div>
                              </div>
                              <div className="detail-item">
                                <div className="detail-label">Created</div>
                                <div className="detail-value">{new Date(pass.created_time).toLocaleDateString()}</div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="pass-footer">
                            <div className="download-actions">
                              <Button
                                variant="primary"
                                onClick={() => handleDownloadPass(pass.id, 'image')}
                                className="download-btn primary"
                              >
                                <i className="fas fa-download me-2"></i>
                                Download
                              </Button>
                              <Button
                                variant="outline-primary"
                                onClick={() => handleDownloadPass(pass.id, 'pdf')}
                                className="download-btn secondary"
                              >
                                <i className="fas fa-file-pdf me-2"></i>
                                PDF
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">
                        <i className="fas fa-ticket-alt"></i>
                      </div>
                      <h3>No Passes Found</h3>
                      <p>No passes were found for this contact information.</p>
                    </div>
                  )}

                  <div className="text-center mt-4">
                    <Button variant="outline-primary" onClick={handleReset}>
                      <i className="fas fa-redo me-2"></i>
                      Verify Another Contact
                    </Button>
                  </div>
                </div>
              </div>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SecurePassAccessPage;

